import { FC, useMemo } from 'react'
import Select from 'react-select'
import { LanguageHelp } from '../../../helpers/LanguageHelper'
import { GeoLanguageCountryOption } from '../../../helpers/GeoLanguageHelper'
import { useThemeMode } from '../../layout/theme-mode/ThemeModeProvider'

type Props = {
  className?: string
  options?: GeoLanguageCountryOption[]
  selectedKey?: string
  allLabel?: string
  placeholder?: string
  onChange: (key: string) => void
  minWidth?: string
  controlMinHeight?: string
}

const GeoLanguageCountrySelect: FC<Props> = ({
  className = '',
  options = [],
  selectedKey = '',
  allLabel = '',
  placeholder = '',
  onChange,
  minWidth = '170px',
  controlMinHeight = '34px',
}) => {
  const { mode } = useThemeMode()

  const selectOptions = useMemo(
    () => options.map((option) => ({ value: option.key, label: option.label })),
    [options]
  )

  const selectedOption = useMemo(() => {
    const matched = selectOptions.find((option) => option.value === selectedKey)
    if (matched) return matched
    if (!selectedKey) {
      return { value: '', label: allLabel || placeholder }
    }
    return null
  }, [allLabel, placeholder, selectOptions, selectedKey])

  return (
    <div className={className}>
      <Select
        className={mode === 'light' ? 'basic-single' : 'basic-single dark'}
        classNamePrefix='select'
        options={selectOptions}
        value={selectedOption}
        isSearchable={true}
        noOptionsMessage={() => LanguageHelp('No matching records found')}
        placeholder={placeholder || allLabel}
        onChange={(option) => onChange(option?.value ?? '')}
        styles={{
          control: (base) => ({
            ...base,
            backgroundColor: mode === 'light' ? '#f5f8fa' : '#2b2b40',
            borderWidth: '0px',
            borderRadius: '5px',
            minWidth,
            cursor: 'pointer',
            fontSize: '12px',
            minHeight: controlMinHeight,
          }),
          menu: (base) => ({
            ...base,
            color: mode === 'light' ? '#7e8299 !important' : '#6d6d80 !important',
            backgroundColor: mode === 'light' ? '#f5f8fa' : '#1e1e2d',
            boxShadow: '0px 0px 30px rgba(0, 0, 0, 0.3)',
            paddingLeft: '0px',
            paddingRight: '1px',
            cursor: 'pointer',
            zIndex: 20,
          }),
          input: (base) => ({
            ...base,
            color: mode === 'light' ? '#7e8299 !important' : '#6d6d80 !important',
          }),
          singleValue: (base) => ({
            ...base,
            color: mode === 'light' ? '#7e8299 !important' : '#6d6d80 !important',
          }),
          indicatorSeparator: (base) => ({ ...base, display: 'none' }),
          dropdownIndicator: (base) => ({
            ...base,
            color: mode === 'light' ? '#7e8299 !important' : '#6d6d80 !important',
          }),
        }}
      />
    </div>
  )
}

export { GeoLanguageCountrySelect }
